import React, { Key, useCallback, useEffect, useMemo, useState } from 'react';

import User from '../../interfaces/User';
import { UserService } from '../../services/userService';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import TruncatedTooltip from './TruncatedTooltip';
import { useTranslation } from 'react-i18next';
import { GetUsersPayload } from '../../interfaces/UserService';
import { useAuth } from '../../hooks/useAuth';
import { Avatar, Select } from 'antd';

export interface Props {
  label?: string;
  description?: string | null;
  errorMessage?: string | (() => string);
  className?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  selectedKey?: Key | null;
  selectedUser?: User | null;
  onChange?: (key: Key | null, user: User | null) => void;
  placeholder?: string;
  allowClear?: boolean;
  excludeUserIds?: number[];
}

const userService = new UserService();

export const UserSelect: React.FC<Props> = ({
  label,
  errorMessage,
  className,
  selectedKey,
  selectedUser,
  onChange,
  isDisabled,
  placeholder,
  allowClear,
  excludeUserIds,
}) => {
  const { t } = useTranslation();
  const { onBehalfOfUser } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState(false);
  const [userFilters, setUserFilters] = useState<GetUsersPayload>({
    count: 10,
    offset: 0,
    search: '',
    roleFilter: [UserRoleEnum.Admin, UserRoleEnum.Contributor],
    ignoreBehalf: false,
    onBehalfOf: onBehalfOfUser?.userId || undefined,
  });
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [resolvedUser, setResolvedUser] = useState<User | null>(
    selectedUser || null
  );

  /**
   * Retrieves a list of users based on the provided filters and updates the state accordingly.
   *
   * @function
   * @param {FilterState} newFilters - The filters to apply when fetching the users.
   * @param {boolean} [reset=false] - Indicates whether to reset the users list or append to it.
   * @returns {Promise<void>} A promise that resolves once the users have been fetched and the state has been updated.
   */
  const getUsers = useCallback(
    async (newFilters: GetUsersPayload, reset = false) => {
      setIsLoading(true);
      await userService
        .getUsers(newFilters)
        .then((res) => {
          const result = res?.users.filter(
            (user) => !excludeUserIds?.includes(user.userId)
          );

          if (reset) {
            setUsers(result);
          } else {
            setUsers((prevUsers) => [...prevUsers, ...result]);
          }
          // Adjust total count by subtracting excluded users
          const excludedCount = excludeUserIds?.length || 0;
          setTotalUsers(Math.max(0, res.total - excludedCount));
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    },
    [excludeUserIds]
  );

  /**
   * A callback function that handles the focus event.
   *
   * This function checks if data has already been fetched.
   * If not, it triggers the `getUsers` function with specific filters
   * and updates the state to indicate that the fetch operation has been performed.
   *
   * @function
   */
  const handleFocus = () => {
    if (!hasInitiallyFetched) {
      void getUsers(userFilters, true);
      setHasInitiallyFetched(true);
    }
  };

  /**
   * A callback function that handles loading more users or groups based on the current state and filters.
   *
   * This function first checks if additional users can be loaded based on the current filters and state,
   * then proceeds to adjust the user filters and load more users if applicable. Similarly, it checks if
   * additional groups can be loaded based on the current filters and state, and then adjusts the group filters
   * and loads more groups if needed. It ensures not to load more users or groups if they are already being loaded.
   *
   * Dependencies:
   * - includeUsers: Boolean indicating whether to load users.
   * - includeGroups: Boolean indicating whether to load groups.
   * - users.length: Number of currently loaded users.
   * - totalUsers: Total number of users available to load.
   * - groups.length: Number of currently loaded groups.
   * - totalGroups: Total number of groups available to load.
   * - isLoading: Boolean indicating if users are already being loaded.
   * - isLoadingGroups: Boolean indicating if groups are already being loaded.
   * - userFilters: Current filters applied to user loading.
   * - groupFilters: Current filters applied to group loading.
   */
  const handleLoadMore = useCallback(() => {
    // Check if there are still users to load
    if (users.length < totalUsers && !isLoading) {
      const newFilters = {
        ...userFilters,
        offset: userFilters.offset + userFilters.count,
      };
      setUserFilters(newFilters);
      void getUsers(newFilters, false);
    }
  }, [users.length, totalUsers, isLoading, userFilters, getUsers]);

  /**
   * Handles the input change event for a search or filter field, applying a debounced update to the user filters and invoking the appropriate API call.
   *
   * This function ensures there is a delay between consecutive input changes to minimize excessive API calls or state updates. The debounce delay is set to 300 milliseconds. When called, the function:
   * - Clears any previously-scheduled debounce timer.
   * - Sets a new debounce timer to update the user filters and initiate a user data fetch after the specified delay.
   *
   * @param {string} value - The new input value that will be used to update the search filter.
   */
  const handleInputChange = (value: string) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      const newFilters = {
        ...userFilters,
        offset: 0,
        search: value,
      };
      setUserFilters(newFilters);
      void getUsers(newFilters, true);
    }, 300);

    setDebounceTimer(timer);
  };

  useEffect(() => {
    setUserFilters((f) => ({
      ...f,
      onBehalfOf: onBehalfOfUser?.userId || undefined,
    }));
  }, [onBehalfOfUser]);

  useEffect(() => {
    if (selectedKey && !resolvedUser) {
      userService
        .getUser(parseInt(selectedKey.toString()))
        .then((response: User) => {
          setResolvedUser(response);
        })
        .catch((error) => {
          console.error("Errore nel recupero dell'utente", error);
        });
    }
  }, [selectedKey]);

  /**
   * A memoized variable that determines the currently selected value based on user and key conditions.
   * Provides a formatted `value` and `label` object if either a user or key is selected and resolved, or `null` otherwise.
   *
   * Dependencies:
   * - `selectedUser`: The currently selected user, if available.
   * - `selectedKey`: A key associated with a potential user, if available.
   * - `resolvedUser`: The resolved user object when a selection is made based on a key.
   *
   * Computed Value:
   * - If `selectedUser` is defined, returns an object containing:
   *   - `value`: The user ID of the selected user.
   *   - `label`: A concatenation of the user's first name and last name.
   * - If `selectedKey` is present and `resolvedUser` is available, returns an object containing:
   *   - `value`: The user ID of the resolved user.
   *   - `label`: A concatenation of the resolved user's first name and last name.
   * - Returns `null` if neither `selectedUser` nor a combination of `selectedKey` and `resolvedUser` is available.
   */
  const selectedValue = useMemo(() => {
    if (selectedUser) {
      return {
        value: selectedUser.userId,
        label: `${selectedUser.firstName} ${selectedUser.lastName}`,
      };
    } else if (selectedKey && resolvedUser) {
      return {
        value: resolvedUser.userId,
        label: `${resolvedUser.firstName} ${resolvedUser.lastName}`,
      };
    } else {
      return null;
    }
  }, [selectedUser, selectedKey, resolvedUser]);

  return (
    <Select
      showSearch
      labelInValue
      onFocus={handleFocus}
      value={selectedValue}
      loading={isLoading}
      allowClear={allowClear}
      prefix={
        selectedValue ? (
          <strong className="text-blue-dark">{t('common.onBehalfOf')}:</strong>
        ) : undefined
      }
      placeholder={placeholder || t('Select a user')}
      notFoundContent={isLoading ? t('Loading...') : null}
      onSearch={handleInputChange}
      onPopupScroll={(e) => {
        const target = e.target as HTMLDivElement;
        if (target.scrollTop + target.clientHeight === target.scrollHeight) {
          handleLoadMore();
        }
      }}
      onChange={(value, record) => {
        const selectedUser = Array.isArray(record)
          ? (record?.[0]?.data as User)
          : (record?.data as User);
        onChange?.(selectedUser?.userId || null, selectedUser || null);
      }}
      filterOption={false}
      options={users.map((user) => ({
        value: user.userId,
        label: `${user.firstName} ${user.lastName}`,
        data: user,
        disabled: excludeUserIds?.includes(user.userId),
      }))}
      optionRender={(option) => (
        <div className="flex w-full max-w-full flex-row items-center gap-2">
          <Avatar
            size={32}
            className="flex flex-shrink-0 items-center justify-center bg-azure-hover uppercase"
          >
            {option?.data?.data?.firstName?.charAt(0)?.toUpperCase() ||
              option?.data?.data?.lastName?.charAt(0)?.toUpperCase() ||
              option?.data?.data?.email?.charAt(0)?.toUpperCase()}
          </Avatar>

          <div className="flex min-w-0 flex-col truncate">
            <span className="truncate text-blue-dark">
              <TruncatedTooltip>
                {option?.data?.data?.firstName} {option?.data?.data?.lastName}
              </TruncatedTooltip>
            </span>
            <div className="truncate text-sm text-gray-dark">
              <TruncatedTooltip>{option?.data?.data?.email}</TruncatedTooltip>
            </div>
          </div>
        </div>
      )}
      className={className}
      disabled={isDisabled}
    />
  );
};
